import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';

import { Button, ButtonType } from '../../../components';
import { DAYS_OF_WEEK, DayOfWeek, TimeOfDay, TIMES_OF_DAY } from '../constants';
import { EditAvailabilityModal } from './EditAvailabilityModal';

const parseTimeIds = (raw: string | null): Set<number> => {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => Number(s))
      .filter((n) => !Number.isNaN(n)),
  );
};

// Mirrors sitters-availability-filter.tpl.html — a compact week-grid summary
// of the current recurring availability filter. Clicking anywhere on the grid
// or the Edit link opens the detailed edit modal. Read-only; the modal is
// where users toggle individual cells.
export const AvailabilityFilter = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);

  const isAvailable = (day: DayOfWeek, time: TimeOfDay): boolean => {
    const ids = parseTimeIds(searchParams.get(day.filterKey));
    return ids.has(time.id);
  };

  const exactRecurring = searchParams.get('exactRecurringAvailability') === 'true';

  return (
    <section className='mb-4'>
      <h4 className='font-semibold mb-1'>Generally available</h4>
      <Button
        buttonType={ButtonType.tertiary}
        onClick={(): void => setEditOpen(true)}
        className='w-full !text-left !rounded border border-neutral-200 p-2 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sapphire'
        aria-label='Edit availability filter'
      >
        <div className='grid grid-cols-7 text-center text-xs text-neutral-600 mb-1'>
          {DAYS_OF_WEEK.map((d) => (
            <div key={`h-${d.code}`} aria-hidden='true'>{d.code.charAt(0)}</div>
          ))}
        </div>
        {(['MORNING', 'AFTERNOON'] as const).map((timeKey) => (
          <div key={timeKey} className='grid grid-cols-[auto_repeat(7,1fr)] items-center gap-x-1 mb-1'>
            <div className='text-[10px] text-neutral-500 w-5'>
              {timeKey === 'MORNING' ? 'AM' : 'PM'}
            </div>
            {DAYS_OF_WEEK.map((d) => (
              <div key={`${timeKey}-${d.code}`} className='text-center'>
                <FontAwesomeIcon
                  icon={faSun}
                  className={
                    isAvailable(d, TIMES_OF_DAY[timeKey])
                      ? 'text-yellow-500'
                      : 'text-neutral-300'
                  }
                />
              </div>
            ))}
          </div>
        ))}
        <div className='grid grid-cols-[auto_repeat(7,1fr)] items-center gap-x-1'>
          <div className='w-5' />
          {DAYS_OF_WEEK.map((d) => (
            <div key={`NIGHT-${d.code}`} className='text-center'>
              <FontAwesomeIcon
                icon={faMoon}
                className={
                  isAvailable(d, TIMES_OF_DAY.NIGHT)
                    ? 'text-sapphire'
                    : 'text-neutral-300'
                }
              />
            </div>
          ))}
        </div>
      </Button>
      <div className='flex items-center justify-between mt-2 text-sm'>
        <span className='text-neutral-600'>
          {exactRecurring ? 'Showing exact matches' : 'Showing partial matches'}
        </span>
        <Button
          buttonType={ButtonType.tertiary}
          id='edit-availability-button'
          onClick={(): void => setEditOpen(true)}
          className='font-medium underline inline-flex items-center gap-1'
        >
          <FontAwesomeIcon icon={faPenToSquare} />
          Edit
        </Button>
      </div>

      <EditAvailabilityModal
        isOpen={editOpen}
        onClose={(): void => setEditOpen(false)}
      />
    </section>
  );
};
